import { SECTION_CARD } from '@/components/ui/section-card-variants'
import Skeleton from '@/components/ui/Skeleton'
import { BROKERS_BY_SECTOR_LIMIT } from '@/features/brokers/api/queries'
import { cn } from '@/lib/utils'
import {
  BROKER_COLUMN_WIDTH,
  GRID_TABLE,
  HEAD_ROW_HEIGHT,
  SCROLL_WRAPPER,
  SECTOR_COLUMN_WIDTH,
  SKELETON_VIEWPORT,
  STICKY_CELL,
  TILE_HEIGHT,
} from '@/screens/brokers/components/sector-flow/by-sector-grid'

/**
 * Sector columns drawn while loading: the typical day's count (15 traded on
 * dev's day, of NEPSE's 13 sub-indices plus debentures and promoter shares).
 * The count only sets how far the skeleton runs off the right edge, never its
 * height, but it has to run off at every width the loaded grid does, so that
 * a platform that draws a horizontal scrollbar draws it under both.
 */
const TYPICAL_SECTORS = 15

const COLUMNS = Array.from({ length: TYPICAL_SECTORS }, (_, index) => index)
const ROWS = Array.from({ length: BROKERS_BY_SECTOR_LIMIT }, (_, index) => index)

/**
 * The "Broker by sector" card's body while it loads: the subtitle line, the
 * grid and the legend, built from the loaded card's own boxes
 * (`by-sector-grid.ts`) so the two match at every width. A real `<table>`
 * with the same spacing, the same sticky 112/176px Broker column and 112px
 * sector columns, the same 32px head row and twelve 32px tile rows (the card
 * asks for `BROKERS_BY_SECTOR_LIMIT`), in a box built like `ScrollArea`'s
 * without importing it. Measured in the harness, pending page against loaded
 * page, at 1440 and 390. Bars only, no words.
 */
export function BrokerBySectorBodySkeleton() {
  return (
    <div className="@container/bysector flex flex-col gap-4">
      <div className="flex h-5 items-center">
        <Skeleton className="h-3 w-72 max-w-full rounded-xs" />
      </div>
      <div aria-hidden="true" className={cn('relative flex', SCROLL_WRAPPER)}>
        <div className={SKELETON_VIEWPORT}>
          <table className={GRID_TABLE}>
            {/* The loaded table's `sr-only` caption still costs Chrome one 4px
                border-spacing gap in a separated-borders table; an empty one
                here keeps the two tables the same height. */}
            <caption className="sr-only" />
            <thead>
              <tr>
                <th className={cn(HEAD_ROW_HEIGHT, STICKY_CELL)}>
                  <div className={BROKER_COLUMN_WIDTH}>
                    <Skeleton className="h-3 w-12 rounded-xs" />
                  </div>
                </th>
                {COLUMNS.map((index) => (
                  <th key={index} className={cn(HEAD_ROW_HEIGHT, 'p-0')}>
                    <div className={cn(SECTOR_COLUMN_WIDTH, 'ml-auto pr-2')}>
                      <Skeleton className="ml-auto h-3 w-12 rounded-xs" />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row) => (
                <tr key={row}>
                  <th className={STICKY_CELL}>
                    <div className={cn(TILE_HEIGHT, BROKER_COLUMN_WIDTH, 'flex items-center')}>
                      <Skeleton className="h-4 w-20 rounded-xs" />
                    </div>
                  </th>
                  {COLUMNS.map((index) => (
                    <td key={index} className={cn(SECTOR_COLUMN_WIDTH, 'p-0')}>
                      <Skeleton className={cn(TILE_HEIGHT, 'w-full rounded-none')} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="flex h-5 items-center justify-center gap-2">
        <Skeleton className="h-3 w-40 max-w-full rounded-xs" />
      </div>
    </div>
  )
}

/**
 * The whole card, for `SectorFlowScreen`'s route pending page: the title row
 * at a card head's 36px (`SectionHead size="card"`), then `SectionCard`'s
 * `mt-3` and the body above.
 */
export default function BrokerBySectorSkeleton() {
  return (
    <div className={SECTION_CARD}>
      <div className="flex min-h-9 items-center">
        <Skeleton className="h-4 w-36 rounded-xs" />
      </div>
      <div className="mt-3">
        <BrokerBySectorBodySkeleton />
      </div>
    </div>
  )
}
